import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Tooltip,
  Select,
  IconButton,
  Container,
  Divider,
  Button,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import { isDemoUser } from '../utils/demoUtils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterListIcon from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarRateIcon from '@mui/icons-material/StarRate';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLocation, useNavigate } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { colors, spacing } from '../utils/themeUtils';
import { SortOptionContext } from '../context/SortOptionProvider';
import { NavigationContext } from '../components/Layout';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StarHalfIcon from '@mui/icons-material/StarHalf';

const timeframeOptions = [
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "365", label: "Last 365 Days" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" }
];

// Default source options as fallback
const defaultSourceOptions = [
  { value: "all", label: "All Sources" }
];

const Header = ({ userRole }) => {
  const { selectedBusinesses, currencySymbol } = useContext(BusinessContext);
  const { 
    timeframe, 
    setTimeframe, 
    setSelectedDate, 
    view, 
    setView, 
    sortOption, 
    setSortOption, 
    selectedCategory,
    setSelectedCategory,
    selectedSubject,
    setSelectedSubject,
    selectedAttribute,
    setSelectedAttribute,
    selectedDateRange,
    setSelectedDateRange,
    selectedSources,
    setSelectedSources,
    resetFilters,
    overviewStats,
    fetchOverviewStats
  } = useContext(ReviewsContext);
  const { isPortrait, showSidebar, handleMenuClick } = useContext(NavigationContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
  const [ratingValue, setRatingValue] = useState(null);
  const [signupDate, setSignupDate] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [sourceOptions, setSourceOptions] = useState(defaultSourceOptions);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-width: 900px)');

  // Calculate derived values from selectedBusinesses
  const numSelected = selectedBusinesses?.length || 0;
  const singleSelectedBusiness = numSelected === 1 ? selectedBusinesses[0] : null;

  const aggregateTurnover = useMemo(() => {
    if (numSelected === 0) return null;
    return selectedBusinesses.reduce((sum, biz) => sum + (biz.turnover || 0), 0);
  }, [selectedBusinesses, numSelected]);

  // Check if businesses have different currencies
  const hasMixedCurrencies = useMemo(() => {
    if (numSelected <= 1) return false;
    const currencies = selectedBusinesses
      .map(biz => biz.currency)
      .filter(currency => currency != null);
    if (currencies.length === 0) return false;
    return new Set(currencies).size > 1;
  }, [selectedBusinesses, numSelected]);

  const averageRating = useMemo(() => {
    if (numSelected === 0) return null;
    
    // Use header_rating from overviewStats if available (always all-time, source-filtered)
    if (overviewStats?.header_rating !== null && overviewStats?.header_rating !== undefined) {
      return overviewStats.header_rating;
    }
    
    // Fall back to business ratings when overviewStats not available
    const businessesWithRating = selectedBusinesses.filter(biz => biz.rating !== null && biz.rating !== undefined);
    if (businessesWithRating.length === 0) return null;
    const sumRatings = businessesWithRating.reduce((sum, biz) => sum + biz.rating, 0);
    return sumRatings / businessesWithRating.length;
  }, [selectedBusinesses, numSelected, overviewStats]);

  // Format the signup date for display
  const formattedDate = signupDate ? signupDate.toLocaleDateString() : '';

  // Effect to update rating value based on selection
  useEffect(() => {
    if (numSelected >= 1) {
        // Always use averageRating as it handles source filtering correctly
        setRatingValue(averageRating);
    } else {
        setRatingValue(null);
    }
  }, [selectedBusinesses, numSelected, averageRating]);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUserEmail(userData.email);
          // Check if this is the demo user
          setIsDemoMode(userData.email === 'info@sashy.ai');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    fetchUserInfo();
  }, []);

  // Fetch available sources when selected businesses change
  useEffect(() => {
    const fetchAvailableSources = async () => {
      if (!selectedBusinesses || selectedBusinesses.length === 0) {
        setSourceOptions(defaultSourceOptions);
        return;
      }

      setIsLoadingSources(true);
      try {
        const businessIds = selectedBusinesses.map(b => b.id);
        const params = new URLSearchParams();
        businessIds.forEach(id => params.append('selected_business_ids', id));
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/reviews/sources?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const sources = await response.json();
          setSourceOptions(sources);
          
          // If current selected sources are no longer available, reset to "all"
          if (selectedSources && selectedSources.length > 0) {
            const availableValues = sources.map(s => s.value);
            const validSources = selectedSources.filter(source => availableValues.includes(source));
            if (validSources.length === 0) {
              setSelectedSources(["all"]);
            } else if (validSources.length !== selectedSources.length) {
              setSelectedSources(validSources);
            }
          }
        } else {
          console.error('Failed to fetch available sources');
          setSourceOptions(defaultSourceOptions);
        }
      } catch (error) {
        console.error('Error fetching available sources:', error);
        setSourceOptions(defaultSourceOptions);
      } finally {
        setIsLoadingSources(false);
      }
    };

    fetchAvailableSources();
  }, [selectedBusinesses, setSelectedSources]);

  // Fetch overview stats when businesses or sources change (for header rating calculation - independent of timeframe)
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      // Only fetch if we're on a page that needs overview stats and avoid duplicate calls
      const currentPath = location.pathname;
      const needsOverviewStats = currentPath === '/reviews' || currentPath === '/dashboard' || currentPath === '/';
      
      if (needsOverviewStats) {
        // Add a small delay to prevent simultaneous API calls with other components
        const timer = setTimeout(() => {
          fetchOverviewStats();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [selectedBusinesses, selectedSources, fetchOverviewStats, location.pathname]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountMenuClick = (event) => {
    setAccountMenuAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        // Clear all user-specific localStorage data
        localStorage.removeItem('selectedBusinesses');
        localStorage.removeItem('currencySymbol');
        localStorage.removeItem('selectedDateRange');
        localStorage.removeItem('isCustomDateRange');
        localStorage.removeItem('timeframe');
        localStorage.removeItem('view');
        localStorage.removeItem('sortOption');
        localStorage.removeItem('selectedSources');
        
        window.location.href = '/login';
      } else {
        console.error('Logout failed');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      handleClose();
      handleAccountMenuClose();
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/payments/customer-portal`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error("No URL returned from customer portal endpoint");
      }
    } catch (error) {
      console.error("Error creating customer portal session:", error);
    }
    handleAccountMenuClose();
  };

  const handleBackToLanding = async () => {
    try {
      // Log out of demo account
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        // Clear all localStorage data including demo selections
        localStorage.clear();
        
        // Redirect to landing page
        window.location.href = '/';
      } else {
        console.error('Logout failed');
        // Still clear localStorage and redirect on logout failure
        localStorage.clear();
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Still clear localStorage and redirect on error
      localStorage.clear();
      window.location.href = '/';
    } finally {
      handleAccountMenuClose();
    }
  };

  const handleTimeframeChange = (event) => {
    // Stop event propagation to prevent document click handler from clearing selections
    if (event && event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }
    
    const selectedValue = event.target.value;
    
    if (selectedValue === "custom") {
      // Open custom date range dialog
      setCustomDateDialogOpen(true);
      // Set temp dates from current range if exists
      if (selectedDateRange) {
        setTempStartDate(dayjs(selectedDateRange.start));
        setTempEndDate(dayjs(selectedDateRange.end));
      } else {
        // Default to last 365 days
        const endDate = dayjs();
        const startDate = dayjs().subtract(365, 'day');
        setTempStartDate(startDate);
        setTempEndDate(endDate);
      }
    } else {
      // For standard timeframes, just set the timeframe - date ranges will be calculated dynamically
      setSelectedDateRange(null); // Clear any existing custom date range
      setSelectedDate(null);
      setTimeframe(selectedValue);
    }
  };

  const handleCustomDateSave = () => {
    if (tempStartDate && tempEndDate) {
      const startDateObj = dayjs(tempStartDate);
      const endDateObj = dayjs(tempEndDate);
      
      if (startDateObj.isBefore(endDateObj) || startDateObj.isSame(endDateObj, 'day')) {
        setSelectedDateRange({ 
          start: startDateObj.toDate(), 
          end: endDateObj.toDate() 
        });
        setTimeframe("custom");
        setSelectedDate(null);
        setCustomDateDialogOpen(false);
      } else {
        // Handle error - start date must be before end date
        alert("Start date must be before or equal to end date");
      }
    }
  };

  const handleCustomDateCancel = () => {
    setCustomDateDialogOpen(false);
    setTempStartDate(null);
    setTempEndDate(null);
    // Reset timeframe to previous value if it was custom
    if (timeframe === "custom" && !selectedDateRange) {
      setTimeframe("30"); // Default fallback
    }
  };

  // Get display value for timeframe selector
  const getTimeframeDisplayValue = () => {
    if (timeframe === "custom" && selectedDateRange) {
      return "custom";
    }
    return timeframe;
  };

  // Get display text for timeframe selector
  const getTimeframeDisplayText = () => {
    // Only show date range for custom timeframe selections
    if (timeframe === "custom" && selectedDateRange) {
      return `${selectedDateRange.start.toLocaleDateString()} - ${selectedDateRange.end.toLocaleDateString()}`;
    }
    // For standard timeframes, show the original label
    const option = timeframeOptions.find(opt => opt.value === timeframe);
    return option ? option.label : timeframe;
  };

  const handleViewChange = (event, newValue) => {
    // Stop event propagation to prevent document click handler from clearing selections
    if (event && event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }
    
    if (newValue !== null) {
      setView(newValue);
    }
  };

  const handleSortOptionChange = (event, newValue) => {
    if (newValue !== null) {
      setSortOption(newValue);
    }
  };

  const handleSourceChange = (event, newValue) => {
    if (newValue !== null) {
      // If current selection is ["all"] and user clicked a specific source, switch to that source
      if (selectedSources.includes("all") && selectedSources.length === 1 && newValue.length > 1) {
        const specificSources = newValue.filter(source => source !== "all");
        setSelectedSources(specificSources);
      }
      // If "all" was just clicked (and other sources were selected), switch to just "all"
      else if (newValue.includes("all") && !selectedSources.includes("all")) {
        setSelectedSources(["all"]);
      }
      // If no sources selected, default to "all"
      else if (newValue.length === 0) {
        setSelectedSources(["all"]);
      }
      // Otherwise, use the selection but remove "all" if other sources are present
      else {
        const filteredSources = newValue.filter(source => source !== "all");
        setSelectedSources(filteredSources.length > 0 ? filteredSources : newValue);
      }
    }
  };

  const handleIndividualSourceClick = (sourceValue) => {
    // If clicking "all", set to just ["all"]
    if (sourceValue === "all") {
      setSelectedSources(["all"]);
      return;
    }

    // If current selection is ["all"], switch to just the clicked source
    if (selectedSources.includes("all") && selectedSources.length === 1) {
      setSelectedSources([sourceValue]);
      return;
    }

    // If the source is already selected, remove it
    if (selectedSources.includes(sourceValue)) {
      const newSources = selectedSources.filter(source => source !== sourceValue);
      // If no sources left, default to "all"
      setSelectedSources(newSources.length === 0 ? ["all"] : newSources);
      return;
    }

    // If the source is not selected, add it (and remove "all" if present)
    const currentSources = selectedSources.filter(source => source !== "all");
    const newSources = [...currentSources, sourceValue];
    
    // Check if all available sources are now selected
    const availableSources = sourceOptions.filter(option => option.value !== "all").map(option => option.value);
    const allSourcesSelected = availableSources.every(source => newSources.includes(source));
    
    // If all sources are selected, switch to "All Sources" instead
    if (allSourcesSelected) {
      setSelectedSources(["all"]);
    } else {
      setSelectedSources(newSources);
    }
  };

  // Helper function to get source icon if it exists
  const getSourceIcon = (sourceValue) => {
    const formats = ['svg', 'png', 'jpg', 'jpeg'];
    
    for (const format of formats) {
      try {
        // Try to import the icon dynamically
        return require(`../assets/icons/sources/${sourceValue.toLowerCase()}.${format}`);
      } catch (error) {
        // Continue to next format
        continue;
      }
    }
    
    // No icon found in any format
    return null;
  };

  // Helper function to truncate source labels for square buttons
  const truncateSourceLabel = (label) => {
    if (label.length <= 6) return label;
    // Special cases for common sources
    const specialCases = {
      'All Sources': 'All',
      'Google': 'Google',
      'Yelp': 'Yelp',
      'Just Eat': 'JustEat',
      'Deliveroo': 'Delivr',
      'Uber Eats': 'Uber',
      'Booking.com': 'Book',
      'Design My Night': 'DMN',
      'OpenTable': 'Open',
      'Trustpilot': 'Trust'
    };
    return specialCases[label] || label.substring(0, 6);
  };

  const handleUnlockOngoingAnalysis = async () => {
    try {
      // Direct monthly checkout - using the same endpoint as LandingSubscriptions
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/payment-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const isReviewsPage = location.pathname === '/reviews';
  const isDashboardPage = location.pathname === '/';
  const isSubjectMatrixPage = location.pathname === '/timeline';
  const isRelevantPage = isReviewsPage || isDashboardPage || isSubjectMatrixPage;

  // Determine if controls relevant only to the Timeline page should be shown
  const showMatrixControls = isSubjectMatrixPage;

  return (
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)',
      pt: spacing.headerBarTop,
      pb: spacing.headerBarBottom,
      px: spacing.headerBarHorizontal
    }}>
      <Container maxWidth={false}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, lg: 4 },
          flexWrap: 'nowrap'
        }}>
          {/* Navigation Menu Button - Only in mobile portrait */}
          {!showSidebar && (
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{
                mr: 1,
                display: { xs: 'flex', lg: 'none' },
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                borderRadius: '8px',
                p: 0.75,
                boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                '&:hover': {
                  boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <MenuIcon sx={{ color: '#546E7A', fontSize: '1.3rem' }} />
            </IconButton>
          )}
          
          {/* Business Info Section */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: { xs: '1 1 auto', lg: '0 1 auto' },
            minWidth: 0
          }}>
            <StorefrontIcon sx={{ 
              fontSize: { xs: '1.75rem', lg: '2.5rem' },
              color: '#1976d2',
              flexShrink: 0,
              pt: { xs: 0.2, lg: 0.5 },
              alignSelf: 'flex-start',
            }} />
            <Box sx={{
              minWidth: 0
            }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.25rem', lg: '2rem' },
                  color: '#0A2540',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {numSelected === 1 ? singleSelectedBusiness?.name : numSelected > 1 ? `${numSelected} Businesses Selected` : 'No Business Selected'}
              </Typography>
              {/* Display Business Address */}
              {numSelected === 1 && singleSelectedBusiness?.address && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}>
                  <LocationOnIcon sx={{ 
                    fontSize: { xs: '0.9rem', lg: '1rem' }, 
                    color: '#78909C',
                    alignSelf: 'flex-start',
                    mt: { xs: 0.2, lg: 0.3 }
                  }} />
                  <Typography sx={{
                    color: '#78909C',
                    fontSize: { xs: '0.75rem', lg: '0.9rem' },
                    fontWeight: 400,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>
                    {singleSelectedBusiness.address}
                  </Typography>
                </Box>
              )}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5
              }}>
                {view === "Sales" ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Tooltip title={hasMixedCurrencies ? "Selected businesses use different currencies" : 
                      (numSelected > 1 ? "Combined annual revenue for all selected businesses" : "Annual revenue for the selected business")} arrow>
                      <Typography sx={{
                        color: '#0A2540',
                        fontSize: { xs: '0.9rem', lg: '1.1rem' },
                        fontWeight: 600,
                        display: 'block',
                        cursor: 'help'
                      }}>
                        Revenue: {hasMixedCurrencies ? 'Multiple Currencies' : 
                          (aggregateTurnover !== null ? `${currencySymbol}${aggregateTurnover.toLocaleString()}` : 'N/A')}
                        {numSelected > 1 && aggregateTurnover !== null && !hasMixedCurrencies && ' Total'}
                      </Typography>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Tooltip title={numSelected > 1 ? "Average rating across all selected businesses" : "Rating for the selected business"} arrow>
                      <Typography sx={{
                        color: '#0A2540',
                        fontSize: { xs: '0.9rem', lg: '1.1rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help'
                      }}>
                        Rating: {ratingValue !== null ? ratingValue.toFixed(1) : 'N/A'}
                        {ratingValue !== null && (
                          <Rating
                            value={ratingValue}
                            precision={0.1}
                            readOnly
                            size="small"
                            sx={{
                              color: '#1976d2',
                              '& .MuiRating-iconEmpty': {
                                color: 'rgba(25, 118, 210, 0.3)'
                              },
                              ml: 0.5
                            }}
                          />
                        )}
                        {numSelected > 1 && ratingValue !== null && (
                          <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit', color: 'inherit', ml: 0.5 }}>
                            Avg.
                          </Typography>
                        )}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0
          }}>
            {isRelevantPage && (
              <>
                {/* Source Filter Individual Buttons */}
                <Box
                  data-filter-exempt="true"
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    marginRight: '12px',
                    alignItems: 'center'
                  }}
                >
                  {sourceOptions.map(option => {
                    const isSelected = selectedSources.includes(option.value);
                    return (
                      <Tooltip key={option.value} title={`Filter by ${option.label}`} arrow>
                        <Button
                          onClick={() => handleIndividualSourceClick(option.value)}
                          data-filter-exempt="true"
                          variant={isSelected ? "contained" : "outlined"}
                          sx={{
                            width: '44px',
                            height: '44px',
                            minWidth: '44px',
                            padding: 0,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            lineHeight: 1.2,
                            border: '1px solid rgba(25, 118, 210, 0.2)',
                            background: isSelected 
                              ? 'rgba(25, 118, 210, 0.08)'
                              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                            color: isSelected ? '#1976d2' : '#546E7A',
                            boxShadow: isSelected 
                              ? '0 2px 10px -2px rgba(25, 118, 210, 0.3)'
                              : '0 2px 10px -2px rgba(0,0,0,0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)',
                              borderColor: '#1976d2'
                            },
                            '&:active': {
                              transform: 'scale(0.95)'
                            }
                          }}
                        >
                          {(() => {
                            const iconSrc = getSourceIcon(option.value);
                            if (iconSrc) {
                              return (
                                <img 
                                  src={iconSrc} 
                                  alt={option.label}
                                  style={{ 
                                    width: '24px', 
                                    height: '24px'
                                  }} 
                                />
                              );
                            }
                            return truncateSourceLabel(option.label);
                          })()}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Sort Toggle - Only on Dashboard page */}
                {isDashboardPage && (
                  <ToggleButtonGroup 
                    value={sortOption}
                    exclusive
                    onChange={handleSortOptionChange}
                    data-filter-exempt="true"
                    sx={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      height: '44px',
                      marginRight: '12px',
                      width: { lg: '240px' },
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        minWidth: { xs: '33.3%', lg: '33.3%' },
                        flex: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '&:not(:last-child)': {
                          borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Sort charts by all impacts" arrow>
                      <ToggleButton 
                        value="total" 
                        aria-label="total impact"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        <SwapVertIcon sx={{ mr: 0.5, fontSize: '1.2rem'}} />
                        All
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Sort charts by positive impact" arrow>
                      <ToggleButton 
                        value="positive" 
                        aria-label="positive impact"
                        sx={{
                          color: colors.positiveGreen,
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: colors.positiveGreen,
                          }
                        }}
                      >
                        {view === "Sales" ? `+${currencySymbol}` : "+★"}
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Sort charts by negative impact" arrow>
                      <ToggleButton 
                        value="negative" 
                        aria-label="negative impact"
                        sx={{
                          color: colors.negativeRed,
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: colors.negativeRed,
                          }
                        }}
                      >
                        {view === "Sales" ? `-${currencySymbol}` : "-★"}
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                )}

                {/* View Toggle Group (Revenue/Rating) - Hide on Timeline page */}
                {!isSubjectMatrixPage && (
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    data-filter-exempt="true"
                    data-demo-target="view-toggle"
                    sx={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      height: '44px',
                      width: { lg: '240px' },
                      marginRight: '12px',
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        color: '#546E7A',
                        minWidth: { xs: '50%', lg: '50%' },
                        flex: 1,
                        textTransform: 'capitalize',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: '#1976d2'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '&:not(:last-child)': {
                          borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Show impact on revenue" arrow>
                      <ToggleButton 
                        value="Sales"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        {currencySymbol} Revenue
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Show impact on rating" arrow>
                      <ToggleButton 
                        value="Rating"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        <StarRateIcon sx={{ mr: 0.5, fontSize: '1.2rem'}} />
                        Rating
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                )}

                {/* Timeframe selector with styling like toggle buttons */}
                <Select
                  value={getTimeframeDisplayValue()}
                  onChange={handleTimeframeChange}
                  data-filter-exempt="true"
                  renderValue={(value) => getTimeframeDisplayText()}
                  sx={{
                    height: '44px',
                    width: { lg: '240px' },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px !important',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    marginRight: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      color: '#546E7A',
                      display: 'flex',
                      alignItems: 'center',
                      pl: 2
                    },
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '12px',
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                        border: '1px solid rgba(25, 118, 210, 0.1)'
                      },
                      "data-filter-exempt": "true"
                    }
                  }}
                >
                  {timeframeOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      data-filter-exempt="true"
                      onClick={(e) => {
                        if (option.value === "custom") {
                          // Always open dialog for custom option
                          e.stopPropagation();
                          setCustomDateDialogOpen(true);
                          // Set temp dates from current range if exists
                          if (selectedDateRange) {
                            setTempStartDate(dayjs(selectedDateRange.start));
                            setTempEndDate(dayjs(selectedDateRange.end));
                          } else {
                            // Default to last 365 days
                            const endDate = dayjs();
                            const startDate = dayjs().subtract(365, 'day');
                            setTempStartDate(startDate);
                            setTempEndDate(endDate);
                          }
                        } else {
                          // Reset the month selection when choosing a timeframe
                          e.stopPropagation();
                          setSelectedDate(null);
                        }
                      }}
                      sx={{
                        fontSize: '0.9rem',
                        py: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: '#1976d2',
                          fontWeight: 'bold'
                        }
                      }}
                    >
                      {option.value === "custom" && timeframe === "custom" && selectedDateRange 
                        ? `${selectedDateRange.start.toLocaleDateString()} - ${selectedDateRange.end.toLocaleDateString()}`
                        : option.label
                      }
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {isDemoMode ? (
              <Button
                onClick={handleBackToLanding}
                variant="contained"
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 10px -2px rgba(255, 152, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                    boxShadow: '0 4px 15px -3px rgba(255, 152, 0, 0.4)'
                  }
                }}
              >
                Back to Landing Page
              </Button>
            ) : (
              <Tooltip title="Account settings" arrow>
                <IconButton
                  onClick={handleAccountMenuClick}
                  sx={{
                    width: '44px',
                    height: '44px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <SettingsIcon sx={{
                    color: '#546E7A',
                    fontSize: '1.5rem'
                  }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Mobile Controls */}
          <Box sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            gap: 1,
            flexShrink: 0
          }}>
            {isRelevantPage && (
              <Tooltip title="View options" arrow>
                <IconButton
                  onClick={handleClick}
                  sx={{
                    width: '36px',
                    height: '36px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <FilterListIcon sx={{
                    color: '#546E7A',
                    fontSize: '1.25rem'
                  }} />
                </IconButton>
              </Tooltip>
            )}

            {isDemoMode ? (
              <Button
                onClick={handleBackToLanding}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  boxShadow: '0 2px 10px -2px rgba(255, 152, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                    boxShadow: '0 4px 15px -3px rgba(255, 152, 0, 0.4)'
                  }
                }}
              >
                Back to Landing
              </Button>
            ) : (
              <Tooltip title="Account settings" arrow>
                <IconButton
                  onClick={handleAccountMenuClick}
                  sx={{
                    width: '36px',
                    height: '36px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <SettingsIcon sx={{
                    color: '#546E7A',
                    fontSize: '1.25rem'
                  }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* View Options Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: '12px',
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                width: '260px'
              },
              'data-filter-exempt': 'true'
            }}
          >
            {isRelevantPage && (
              <>
                {/* Source Filter Individual Buttons */}
                <MenuItem sx={{ py: 1.5, px: 2, display: 'flex', justifyContent: 'center' }}>
                  <Box
                    data-filter-exempt="true"
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      maxWidth: '220px'
                    }}
                  >
                    {sourceOptions.map(option => {
                      const isSelected = selectedSources.includes(option.value);
                      return (
                        <Tooltip key={option.value} title={`Filter by ${option.label}`} arrow>
                          <Button
                            onClick={() => handleIndividualSourceClick(option.value)}
                            data-filter-exempt="true"
                            variant={isSelected ? "contained" : "outlined"}
                            sx={{
                              width: '40px',
                              height: '40px',
                              minWidth: '40px',
                              padding: 0,
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              textTransform: 'none',
                              lineHeight: 1.2,
                              border: '1px solid rgba(25, 118, 210, 0.2)',
                              background: isSelected 
                                ? 'rgba(25, 118, 210, 0.08)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                              color: isSelected ? '#1976d2' : '#546E7A',
                              boxShadow: isSelected 
                                ? '0 2px 10px -2px rgba(25, 118, 210, 0.3)'
                                : '0 2px 10px -2px rgba(0,0,0,0.05)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)',
                                borderColor: '#1976d2'
                              },
                              '&:active': {
                                transform: 'scale(0.95)'
                              }
                            }}
                          >
                            {(() => {
                              const iconSrc = getSourceIcon(option.value);
                              if (iconSrc) {
                                return (
                                  <img 
                                    src={iconSrc} 
                                    alt={option.label}
                                    style={{ 
                                      width: '22px', 
                                      height: '22px'
                                    }} 
                                  />
                                );
                              }
                              return truncateSourceLabel(option.label);
                            })()}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </MenuItem>

                {/* Sort Toggle - Only on Dashboard page */}
                {isDashboardPage && (
                  <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                      value={sortOption}
                      exclusive
                      onChange={handleSortOptionChange}
                      size="small"
                      data-filter-exempt="true"
                      sx={{
                        width: '240px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                        border: '1px solid rgba(25, 118, 210, 0.1)',
                        borderRadius: '12px !important',
                        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '& .MuiToggleButton-root': {
                          border: 'none',
                          fontSize: '0.95rem',
                          px: 2,
                          minWidth: '33.3%',
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          },
                          '&:not(:last-child)': {
                            borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                          }
                        }
                      }}
                    >
                      <Tooltip title="Sort charts by all impacts" arrow>
                        <ToggleButton 
                          value="total" 
                          aria-label="total impact"
                          sx={{
                            color: '#546E7A',
                            fontWeight: 'bold',
                            textTransform: 'capitalize',
                            '&.Mui-selected': {
                              color: '#1976d2',
                            }
                          }}
                        >
                          <SwapVertIcon sx={{ mr: 0.5, fontSize: '1.1rem'}} />
                          All
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip title="Sort charts by positive impact" arrow>
                        <ToggleButton 
                          value="positive" 
                          aria-label="positive impact"
                          sx={{
                            color: colors.positiveGreen,
                            fontWeight: 'bold',
                            '&.Mui-selected': {
                              color: colors.positiveGreen,
                            }
                          }}
                        >
                          {view === "Sales" ? `+${currencySymbol}` : "+★"}
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip title="Sort charts by negative impact" arrow>
                        <ToggleButton 
                          value="negative" 
                          aria-label="negative impact"
                          sx={{
                            color: colors.negativeRed,
                            fontWeight: 'bold',
                            '&.Mui-selected': {
                              color: colors.negativeRed,
                            }
                          }}
                        >
                          {view === "Sales" ? `-${currencySymbol}` : "-★"}
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </MenuItem>
                )}

                <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    data-filter-exempt="true"
                    sx={{
                      width: '240px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        fontSize: '0.95rem',
                        px: 2,
                        minWidth: '50%',
                        flex: 1,
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '&:not(:last-child)': {
                          borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Show impact on revenue" arrow>
                      <ToggleButton 
                        value="Sales"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        {currencySymbol} Revenue
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Show impact on rating" arrow>
                      <ToggleButton 
                        value="Rating"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        <StarRateIcon sx={{ mr: 0.5, fontSize: '1.2rem'}} />
                        Rating
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                </MenuItem>

                {/* Timeframe selector with styling like toggle buttons */}
                <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                  <Select
                    value={getTimeframeDisplayValue()}
                    onChange={handleTimeframeChange}
                    data-filter-exempt="true"
                    renderValue={(value) => getTimeframeDisplayText()}
                    sx={{
                      height: '44px',
                      width: '240px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        color: '#546E7A',
                        display: 'flex',
                        alignItems: 'center',
                        pl: 2
                      },
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '12px',
                          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                          border: '1px solid rgba(25, 118, 210, 0.1)'
                        },
                        "data-filter-exempt": "true"
                      }
                    }}
                  >
                    {timeframeOptions.map(option => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        data-filter-exempt="true"
                        onClick={(e) => {
                          if (option.value === "custom") {
                            // Always open dialog for custom option
                            e.stopPropagation();
                            setCustomDateDialogOpen(true);
                            // Set temp dates from current range if exists
                            if (selectedDateRange) {
                              setTempStartDate(dayjs(selectedDateRange.start));
                              setTempEndDate(dayjs(selectedDateRange.end));
                            } else {
                              // Default to last 365 days
                              const endDate = dayjs();
                              const startDate = dayjs().subtract(365, 'day');
                              setTempStartDate(startDate);
                              setTempEndDate(endDate);
                            }
                          } else {
                            // Reset the month selection when choosing a timeframe
                            e.stopPropagation();
                            setSelectedDate(null);
                          }
                        }}
                        sx={{
                          fontSize: '0.9rem',
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: '#1976d2',
                            fontWeight: 'bold'
                          }
                        }}
                      >
                        {option.value === "custom" && timeframe === "custom" && selectedDateRange 
                          ? `${selectedDateRange.start.toLocaleDateString()} - ${selectedDateRange.end.toLocaleDateString()}`
                          : option.label
                        }
                      </MenuItem>
                    ))}
                  </Select>
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>

        {/* Account Settings Menu */}
        <Menu
          anchorEl={accountMenuAnchorEl}
          open={Boolean(accountMenuAnchorEl)}
          onClose={handleAccountMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '12px',
              boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
              minWidth: '200px'
            }
          }}
        >
          {isDemoUser(userEmail) ? (
            // Demo user menu
            <MenuItem onClick={handleBackToLanding} sx={{
              p: 2,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}>
              <Typography sx={{ 
                fontWeight: 500,
                fontSize: '0.95rem' 
              }}>
                Back to Landing Page
              </Typography>
            </MenuItem>
          ) : (
            // Regular user menu
            <>
              <MenuItem onClick={handleManageSubscription} sx={{
                p: 2,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}>
                <Typography sx={{ 
                  fontWeight: 500,
                  fontSize: '0.95rem' 
                }}>
                  Manage Subscription
                </Typography>
              </MenuItem>
              
              <MenuItem onClick={handleLogout} sx={{
                p: 2,
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  color: colors.negativeRed
                }
              }}>
                <Typography sx={{ 
                  fontWeight: 500,
                  fontSize: '0.95rem' 
                }}>
                  Log Out
                </Typography>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Custom Date Range Dialog */}
        <Dialog 
          open={customDateDialogOpen} 
          onClose={handleCustomDateCancel}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tempStartDate && tempEndDate) {
              e.preventDefault();
              handleCustomDateSave();
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 20px 40px -12px rgba(0,0,0,0.3)'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            color: '#0A2540',
            pb: 1
          }}>
            Select Custom Date Range
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 3,
                pt: 1
              }}>
                <DatePicker
                  label="Start Date"
                  value={tempStartDate}
                  onChange={(newValue) => setTempStartDate(newValue)}
                  maxDate={tempEndDate || dayjs()}
                  format="DD/MM/YYYY"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={tempEndDate}
                  onChange={(newValue) => setTempEndDate(newValue)}
                  minDate={tempStartDate}
                  maxDate={dayjs()}
                  format="DD/MM/YYYY"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Box>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={handleCustomDateCancel}
              sx={{ 
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 'bold',
                color: '#78909C'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCustomDateSave}
              variant="contained"
              disabled={!tempStartDate || !tempEndDate}
              sx={{ 
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 4px 15px -3px rgba(25,118,210,0.4)',
                '&:hover': {
                  boxShadow: '0 6px 20px -3px rgba(25,118,210,0.5)'
                }
              }}
            >
              Apply Range
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Header;